import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import './PremiumPage.css';
import { api } from './api';
import { useAuth } from './auth';
import { useI18n } from './i18n';

type Plan = {
  duration_days: number;
  label: string;
  amount: number;
  currency: string;
  amount_display: string;
};

function PremiumPage() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { language, t } = useI18n();
  const copy = t.premium;
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    if (authLoading || !isAuthenticated || user?.is_premium) {
      setPlansLoading(false);
      return;
    }

    api
      .get('/payments/plans/')
      .then((res) => setPlans(res.data.plans))
      .catch(() => setPlansError(copy.plansError))
      .finally(() => setPlansLoading(false));
  }, [authLoading, copy.plansError, isAuthenticated, user?.is_premium]);

  async function handleBuy(durationDays: number) {
    if (!isAuthenticated || user?.is_premium) return;
    setCheckoutError('');
    setCheckoutLoading(durationDays);

    try {
      const res = await api.post('/payments/checkout/', { duration_days: durationDays });
      window.location.href = res.data.checkout_url;
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        copy.checkoutError;
      setCheckoutError(msg);
      setCheckoutLoading(null);
    }
  }

  if (authLoading) {
    return (
      <div className="premium-page">
        <p className="premium-page__loading">{copy.loading}</p>
      </div>
    );
  }

  if (user?.is_premium) {
    return <Navigate to="/profile" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="premium-page">
      <div className="premium-page__header">
        <h1>Premium</h1>
        <p className="premium-page__lead">
          {copy.lead}
        </p>
        {user?.is_premium && (
          <p className="premium-page__active-badge">
            {copy.activeBadge}
            {user.premium_until ? ` ${copy.activeUntil} ${new Date(user.premium_until).toLocaleDateString(language === 'ua' ? 'uk-UA' : language)}` : ''}
          </p>
        )}
      </div>

      {plansLoading && <p className="premium-page__loading">{copy.loadingPlans}</p>}
      {plansError && <p className="premium-page__error">{plansError}</p>}
      {checkoutError && <p className="premium-page__error">{checkoutError}</p>}

      {!plansLoading && !plansError && (
        <div className="premium-page__plans">
          {plans.map((plan) => (
            <div
              key={plan.duration_days}
              className={`premium-page__card${plan.duration_days === 30 ? ' premium-page__card--popular' : ''}`}
            >
              {plan.duration_days === 30 && (
                <span className="premium-page__popular-badge">{copy.popular}</span>
              )}
              <h2 className="premium-page__plan-label">{plan.label}</h2>
              <p className="premium-page__price">{plan.amount_display}</p>
              <ul className="premium-page__features">
                <li>{copy.featureHistory}</li>
                <li>{copy.featureNoAds}</li>
                <li>{copy.featureAllCategories}</li>
                {plan.duration_days === 180 && <li>{copy.featureBestDailyPrice}</li>}
              </ul>

              {isAuthenticated ? (
                <button
                  className="premium-page__buy-button"
                  onClick={() => void handleBuy(plan.duration_days)}
                  disabled={checkoutLoading !== null}
                >
                  {checkoutLoading === plan.duration_days ? copy.redirecting : copy.buyNow}
                </button>
              ) : (
                <Link to="/login" className="premium-page__login-link">
                  {copy.loginToBuy}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="premium-page__hint">
        {copy.stripeHintPrefix}{' '}
        <a href="https://stripe.com" target="_blank" rel="noreferrer">
          Stripe
        </a>
        . {copy.stripeHintSuffix}
      </p>
    </div>
  );
}

export function PremiumSuccessPage() {
  const { t } = useI18n();
  const copy = t.premium;

  return (
    <div className="premium-page">
      <div className="premium-page__result-panel">
        <div className="premium-page__result-icon premium-page__result-icon--success">✓</div>
        <h1>{copy.successTitle}</h1>
        <p>{copy.successText}</p>
        <p className="premium-page__hint">
          {copy.successHint}
        </p>
        <Link to="/" className="premium-page__back-link">
          {copy.backHome}
        </Link>
      </div>
    </div>
  );
}

export function PremiumCancelPage() {
  const { t } = useI18n();
  const copy = t.premium;

  return (
    <div className="premium-page">
      <div className="premium-page__result-panel">
        <div className="premium-page__result-icon premium-page__result-icon--cancel">✕</div>
        <h1>{copy.cancelTitle}</h1>
        <p>{copy.cancelText}</p>
        <Link to="/premium" className="premium-page__back-link">
          {copy.backToPlans}
        </Link>
      </div>
    </div>
  );
}

export default PremiumPage;
